// ArchivedLoanPage.jsx (PROD) — aligned with UAT CE fields
import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';

function formatDateTime(datetime) {
  if (!datetime || datetime === '1970-01-01 00:00:00' || datetime === '0000-00-00 00:00:00') return '-';
  const date = new Date(datetime);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  });
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  const [hours, minutes] = timeStr.split(':');
  const date = new Date();
  date.setHours(parseInt(hours));
  date.setMinutes(parseInt(minutes));
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatDateOnly(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function ArchivedLoanPage({ currentUser }) {
  const [loans, setLoans] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const fetchLoans = async () => {
    try {
      const response = await fetch('https://api.stitchtechsolutions.com/api/list_loans.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: currentUser?.role })
      });
      const data = await response.json();
      if (data.status === 'success') {
        const archived = data.loans.filter(loan => loan.is_archived === "1" || loan.is_archived === 1);
        setLoans(archived);
      } else {
        Swal.fire('Error', data.message, 'error');
      }
    } catch (err) {
      Swal.fire('Error', 'Failed to load archived loans', 'error');
    }
  };

  useEffect(() => { fetchLoans(); }, [currentUser]);

  const onEditChange = (field, value) => setEditingData(prev => ({ ...prev, [field]: value }));
  const onStartEdit = (loanId) => {
    const loan = loans.find(l => l.id === loanId);
    if (loan) {
      setEditingId(loanId);
      setEditingData({ ...loan });
    }
  };

  const onUnarchiveLoan = async (loanId) => {
    const confirm = await Swal.fire({
      title: 'Unarchive this loan?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, unarchive it',
      cancelButtonText: 'Cancel'
    });

    if (confirm.isConfirmed) {
      try {
        const response = await fetch('https://api.stitchtechsolutions.com/api/unarchive_loan.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ loan_id: loanId })
        });
        const result = await response.json();
        Swal.fire(result.status === 'success' ? 'Success' : 'Error', result.message, result.status);
        if (result.status === 'success') fetchLoans();
      } catch (err) {
        Swal.fire('Error', 'Unarchive failed', 'error');
      }
    }
  };

  const handleDeleteLoan = async (loanId) => {
    const confirm = await Swal.fire({
      title: 'Delete this loan permanently?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel'
    });

    if (confirm.isConfirmed) {
      try {
        const response = await fetch('https://api.stitchtechsolutions.com/api/delete_loan.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ loan_id: loanId })
        });
        const result = await response.json();
        Swal.fire(result.status === 'success' ? 'Deleted' : 'Error', result.message, result.status);
        if (result.status === 'success') fetchLoans();
      } catch (err) {
        Swal.fire('Error', 'Delete failed', 'error');
      }
    }
  };

  const onSaveEdit = async () => {
    const response = await fetch('https://api.stitchtechsolutions.com/api/add_or_update_loan.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(editingData)
    });
    const result = await response.json();
    Swal.fire(result.status === 'success' ? 'Success' : 'Error', result.message, result.status);
    if (result.status === 'success') {
      await fetchLoans();
      setEditingId(null);
    }
  };

  const onCancelEdit = () => setEditingId(null);

  const filteredLoans = loans.filter(loan =>
    Object.values(loan).some(val =>
      val?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const isSuperAdmin = currentUser?.role === 'superadmin';

  // ---- UAT-aligned helpers for CE codes ----
  const renderCeOptions = () =>
    Array.from({ length: 21 }, (_, i) => {
      const code = `CE${i.toString().padStart(2, '0')}`;
      return <option key={code} value={code}>{code}</option>;
    });

  const NoEntry = () => (
    <span style={{ opacity: 0.6, fontStyle: 'italic' }}>No entry yet</span>
  );

  return (
    <div>
      <h2>Archived Loans</h2>
      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={e => setSearchTerm(e.target.value)}
        style={{ marginBottom: '1rem', padding: '0.5rem', width: '100%' }}
      />

      <table className="user-table">
        <thead>
          <tr>
            <th>Type of Loan</th>
            <th>Approval Date</th>
            <th>Approval Time</th>
            <th>Region</th>
            {isSuperAdmin && <th>Employee ID</th>}
            {isSuperAdmin && <th>Client Name</th>}
            <th>Initial</th>
            {isSuperAdmin && <th>Verifier</th>}
            <th>Loan Status</th>
            <th>Remarks</th>
            <th>Date and Time Disbursed</th>
            {isSuperAdmin && <th>CE Code LMS</th>}
            <th>CE Code NOAH</th>
            {isSuperAdmin && <th>Time and Date (Time Stamp)</th>}
            {isSuperAdmin && <th>Action</th>}
            {isSuperAdmin && <th>Unarchive</th>}
            {isSuperAdmin && <th>Delete</th>}
          </tr>
        </thead>
        <tbody>
          {filteredLoans.map(loan => {
            const isEditing = loan.id === editingId;
            return (
              <tr key={loan.id}>
                {isEditing
                  ? <td><input value={editingData.type_of_loan || ''} onChange={e => onEditChange('type_of_loan', e.target.value)} /></td>
                  : <td>{loan.type_of_loan}</td>}

                <td>{isEditing
                  ? <input type="date" value={editingData.approval_date || ''} onChange={e => onEditChange('approval_date', e.target.value)} />
                  : formatDateOnly(loan.approval_date)}</td>

                <td>{isEditing
                  ? <input type="time" value={editingData.approval_time || ''} onChange={e => onEditChange('approval_time', e.target.value)} />
                  : formatTime(loan.approval_time)}</td>

                <td>{isEditing
                  ? <input value={editingData.region || ''} onChange={e => onEditChange('region', e.target.value)} />
                  : loan.region}</td>

                {isSuperAdmin && <td>{loan.employee_id}</td>}

                {isSuperAdmin && (isEditing
                  ? <td><input value={editingData.client_name || ''} onChange={e => onEditChange('client_name', e.target.value)} /></td>
                  : <td>{loan.client_name}</td>)}

                <td>{isEditing
                  ? <input value={editingData.initial || ''} onChange={e => onEditChange('initial', e.target.value)} />
                  : loan.initial}</td>

                {isSuperAdmin && <td>{isEditing
                  ? <input value={editingData.verifier || ''} onChange={e => onEditChange('verifier', e.target.value)} />
                  : loan.verifier}</td>}

                <td>{isEditing && isSuperAdmin ? (
                  <select value={editingData.loan_status || ''} onChange={e => onEditChange('loan_status', e.target.value)}>
                    <option value="Pending">Pending</option>
                    <option value="Reprocessing">Reprocessing</option>
                    <option value="For Final Checking">For Final Checking</option>
                    <option value="Not Yet Started">Not Yet Started</option>
                    <option value="Hold">Hold</option>
                    <option value="Disapproved">Disapproved</option>
                    <option value="Disbursed">Disbursed</option>
                    <option value="Cancelled">Cancelled</option>
                    <option value="Archive">Archive</option>
                  </select>
                ) : (
                  <span className={`status-badge ${loan.loan_status?.toLowerCase().replace(/\s/g, '-')}`}>{loan.loan_status}</span>
                )}</td>

                <td>{isEditing
                  ? <input value={editingData.remarks || ''} onChange={e => onEditChange('remarks', e.target.value)} />
                  : loan.remarks}</td>

                <td>{formatDateTime(loan.disbursed_date)}</td>

                {/* CE Code LMS (superadmin only) with fallback */}
                {isSuperAdmin && (
                  <td>
                    {isEditing ? (
                      <select value={editingData.ce_code || ''} onChange={e => onEditChange('ce_code', e.target.value)}>
                        <option value="">--Select--</option>
                        {renderCeOptions()}
                      </select>
                    ) : (
                      loan.ce_code ? loan.ce_code : <NoEntry />
                    )}
                  </td>
                )}

                {/* CE Code NOAH (visible to everyone) with fallback */}
                <td>
                  {isEditing && isSuperAdmin ? (
                    <select value={editingData.ce_code_noah || ''} onChange={e => onEditChange('ce_code_noah', e.target.value)}>
                      <option value="">--Select--</option>
                      {renderCeOptions()}
                    </select>
                  ) : (
                    loan.ce_code_noah ? loan.ce_code_noah : <NoEntry />
                  )}
                </td>

                {isSuperAdmin && <td>{formatDateTime(loan.time_and_date)}</td>}

                {isSuperAdmin && (
                  <td>
                    {isEditing ? (
                      <>
                        <button onClick={onSaveEdit}>Save</button>
                        <button onClick={onCancelEdit}>Cancel</button>
                      </>
                    ) : (
                      <button onClick={() => onStartEdit(loan.id)}>Edit</button>
                    )}
                  </td>
                )}

                {isSuperAdmin && (
                  <td>
                    <button className="btn btn-sm btn-warning" onClick={() => onUnarchiveLoan(loan.id)}>
                      Unarchive
                    </button>
                  </td>
                )}

                {isSuperAdmin && (
                  <td>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteLoan(loan.id)}>
                      Delete
                    </button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default ArchivedLoanPage;
